// Contract Creator Modal — bunker enquiry "with_supply_trader -> recapped"
// transition.
//
// Replaces the generic StageAdvanceModal for this single transition. The
// rest of the enquiry stage flow (rollback, mark-as-lost, archive, draft ->
// with_supply_trader) keeps using StageAdvanceModal — see
// feature_planning/bunkers_contracts/02_contract_creator_modal.md §
// "Replacing the current modal call".
//
// Ownership pattern matches StageAdvanceModal: the modal collects user
// input and calls back to the parent via ``onConfirm``. All API calls
// (advance_stage, file uploads) are owned by the parent (FuelEnquiryForm)
// so the modal stays presentation-only and can be smoke-tested in
// isolation. ``submitting`` and ``error`` are parent-controlled.

import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileSignature,
  faGavel,
  faTrash,
  faUpload,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

import Section from '../ui/Section';
import Field from '../ui/Field';
import { DatePicker, DateRangePicker } from '../ui';
import { fuelEnquiryService } from '../../services/fuelEnquiryService';

import './ContractCreatorModal.css';

// Defaults / constants -------------------------------------------------------

// Bunkers contracts are always Principal on both legs (HMS request, July
// 2026), so Contract Position is a fixed value rather than a dropdown. The
// stored code ('Pr') is unchanged — only the picker is retired — so nothing
// downstream (payload, mint service, backend choices) moves.
const CONTRACT_POSITION_CODE = 'Pr';
const CONTRACT_POSITION_LABEL = 'Principal';

// Per HMS request (May 2026): Contract Type is now trader-selectable so the
// modal can mint Sale/Purchase pairs today and Freight/Recap variants when
// that workflow is wired up. Values stay aligned with
// ``LegalContract.CONTRACT_TYPE_CHOICES`` in backend/legal/models.py — keep
// these in sync when adding new entries.
const CONTRACT_TYPE_OPTIONS = [
  { value: 'Re', label: 'Recaps' },
  { value: 'Sa', label: 'Sales' },
  { value: 'Pu', label: 'Purchase' },
  { value: 'Fr', label: 'Freight' },
];

// Both legs are Principal for bunkers (see CONTRACT_POSITION_CODE above).
// Kept as separate per-leg constants so a future non-bunker variant can
// diverge without touching buildInitialSide.
const SALE_DEFAULT_POSITION = CONTRACT_POSITION_CODE;
const PURCHASE_DEFAULT_POSITION = CONTRACT_POSITION_CODE;

// Default contract_type per panel. Mirrors the legacy hard-coded values
// the mint service used before the dropdown landed, so behaviour for
// traders who don't touch the dropdown is unchanged.
const SALE_DEFAULT_TYPE = 'Sa';
const PURCHASE_DEFAULT_TYPE = 'Pu';

// File constraints. Matches planning doc 02 validation rules; keep these
// constants in sync with the backend file endpoint when Phase 4 ships it.
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
]);
const ALLOWED_EXTENSIONS_HINT = '.pdf, .png, .jpg, .jpeg, .doc, .docx, .txt';

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// Build the initial per-side state. Centralised so the two sides stay in
// sync and we can re-init the form deterministically when ``isOpen``
// toggles back on (the modal is unmounted between opens via the parent's
// conditional render — but keep this idempotent regardless).
const buildInitialSide = (enquiry, side) => {
  const fallbackDate = todayISO();
  return {
    // Sale leg = HMS sell company; Purchase leg = HMS buy company (the
    // entity buying from the supplier), falling back to the sell company
    // when the enquiry left it blank.
    entity_id: side === 'sale'
      ? (enquiry?.hms_sell_company ?? '')
      : (enquiry?.hms_buy_company ?? enquiry?.hms_sell_company ?? ''),
    contract_type: side === 'sale' ? SALE_DEFAULT_TYPE : PURCHASE_DEFAULT_TYPE,
    contract_position: side === 'sale' ? SALE_DEFAULT_POSITION : PURCHASE_DEFAULT_POSITION,
    commence_date: enquiry?.expected_delivery_start || fallbackDate,
    period_start: enquiry?.expected_delivery_start || '',
    period_end: enquiry?.expected_delivery_end || '',
    // Sale leg carries the trader's free-form notes from the enquiry; the
    // Purchase leg is intentionally blank because the supplier side is
    // negotiated separately and HMS rarely echoes the same clauses.
    term_notes: side === 'sale' ? (enquiry?.comments || '') : '',
  };
};

// Validate a file before adding it to the list. Returns an error string or
// null when the file is acceptable.
const validateFile = (file) => {
  if (!file) return 'No file selected.';
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File exceeds the 25 MB limit (got ${formatBytes(file.size)}).`;
  }
  if (ALLOWED_MIME_TYPES.size > 0 && file.type && !ALLOWED_MIME_TYPES.has(file.type)) {
    // Fall back to extension check for browsers that report empty MIME
    // (e.g. Safari with .docx). Accept if the extension matches the
    // hint list.
    const lower = (file.name || '').toLowerCase();
    const extOk = /\.(pdf|png|jpe?g|docx?|txt)$/.test(lower);
    if (!extOk) {
      return `Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS_HINT}.`;
    }
  }
  return null;
};

// --- Sub-component: per-side panel -----------------------------------------

const SidePanel = ({
  side,
  title,
  state,
  setState,
  files,
  onFileAdd,
  onFileRemove,
  hmsEntities,
  errors,
  disabled,
}) => {
  const fileInputId = `legal-contract-files-${side}`;

  // Combine the explicit commence_date with the date range so we can pass
  // a normalised handler to <DatePicker> + <DateRangePicker>. Each emits
  // a synthetic event {target: {name, value}} per the existing UI
  // primitives (see DatePicker.js ~230 and DateRangePicker.js ~267).
  const handleDateEvent = (e) => {
    if (!e?.target?.name) return;
    setState({ ...state, [e.target.name]: e.target.value });
  };

  return (
    <div className={`legal-modal-side legal-modal-side--${side}`}>
      <Section title={title} stepNumber={side === 'sale' ? 1 : 2}>
        <div className="hms-grid hms-grid-2">
          <Field label="Entity" required error={errors?.entity_id}>
            <select
              value={state.entity_id ?? ''}
              onChange={(e) => setState({
                ...state,
                entity_id: e.target.value ? Number(e.target.value) : '',
              })}
              disabled={disabled}
            >
              <option value="">Select entity…</option>
              {hmsEntities.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.hms_entity_name}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Contract Type"
            required
            error={errors?.contract_type}
            hint="Defaults to the leg type — change to Freight or Recaps if needed"
          >
            <select
              value={state.contract_type}
              onChange={(e) => setState({ ...state, contract_type: e.target.value })}
              disabled={disabled}
            >
              {CONTRACT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Contract Position" required error={errors?.contract_position}>
            {/* Fixed to Principal for bunkers — read-only input keeps the
                field's dimensions identical to the sibling controls while
                showing the single valid value. state.contract_position
                stays 'Pr' (set in buildInitialSide), so the payload is
                unchanged. */}
            <input
              type="text"
              value={CONTRACT_POSITION_LABEL}
              readOnly
              disabled={disabled}
            />
          </Field>

          <Field label="Commence Date" required error={errors?.commence_date}>
            <DatePicker
              id={`commence_date_${side}`}
              name="commence_date"
              value={state.commence_date || ''}
              onChange={handleDateEvent}
              format="YYYY-MM-DD"
              disabled={disabled}
              placeholder="Select date"
            />
          </Field>
        </div>

        <Field label="Contract Period" hint="Optional — leave blank for open-ended">
          <DateRangePicker
            startDateName="period_start"
            endDateName="period_end"
            startDateValue={state.period_start || ''}
            endDateValue={state.period_end || ''}
            onChange={handleDateEvent}
            placeholder="Select period…"
            disabled={disabled}
          />
        </Field>

        <Field label="Term Notes" hint="Free-form clauses or remarks">
          <textarea
            value={state.term_notes || ''}
            onChange={(e) => setState({ ...state, term_notes: e.target.value })}
            disabled={disabled}
            rows={3}
            placeholder="e.g. FOB Singapore, payment 30 days net…"
          />
        </Field>

        <Field
          label="Files"
          hint={`Optional — max 25 MB each (${ALLOWED_EXTENSIONS_HINT}).`}
        >
          <div className="legal-modal-files">
            <label
              htmlFor={fileInputId}
              className={`legal-modal-file-drop ${disabled ? 'is-disabled' : ''}`}
            >
              <FontAwesomeIcon icon={faUpload} />
              <span>Click to add files or drop here</span>
              <input
                id={fileInputId}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt"
                onChange={(e) => {
                  const list = Array.from(e.target.files || []);
                  list.forEach(onFileAdd);
                  e.target.value = '';
                }}
                disabled={disabled}
                style={{ display: 'none' }}
              />
            </label>
            {files.length > 0 && (
              <ul className="legal-modal-file-list">
                {files.map((entry) => (
                  <li
                    key={entry.localId}
                    className={`legal-modal-file-row legal-modal-file-row--${entry.status}`}
                  >
                    <FontAwesomeIcon icon={faFileSignature} />
                    <div className="legal-modal-file-row__main">
                      <div className="legal-modal-file-row__name" title={entry.file.name}>
                        {entry.file.name}
                      </div>
                      <div className="legal-modal-file-row__meta">
                        {formatBytes(entry.file.size)}
                        {entry.status === 'queued' && ' • queued'}
                        {entry.status === 'uploading' && ` • uploading ${entry.progress || 0}%`}
                        {entry.status === 'uploaded' && ' • uploaded'}
                        {entry.status === 'failed' && ` • failed: ${entry.error || 'upload error'}`}
                        {entry.status === 'invalid' && ` • ${entry.error}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="legal-modal-file-row__remove"
                      onClick={() => onFileRemove(entry.localId)}
                      disabled={disabled || entry.status === 'uploading'}
                      aria-label={`Remove ${entry.file.name}`}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Field>
      </Section>
    </div>
  );
};

// --- Main modal -------------------------------------------------------------

const ContractCreatorModal = ({
  isOpen,
  enquiry,
  hmsEntities = [],
  onClose,
  onConfirm, // async ({contract_payloads, notes, saleFiles, purchaseFiles}) -> void
  submitting = false,
  error = null,
  saleFiles = [],
  purchaseFiles = [],
  onSaleFilesChange,
  onPurchaseFilesChange,
}) => {
  const [sale, setSale] = useState(() => buildInitialSide(enquiry, 'sale'));
  const [purchase, setPurchase] = useState(() => buildInitialSide(enquiry, 'purchase'));
  const [auditNotes, setAuditNotes] = useState('');
  const [localErrors, setLocalErrors] = useState({ sale: {}, purchase: {} });
  // Bunkers KYC gate (July 2026): recapping is a compulsory compliance step, so
  // the trader must confirm the SECO SESAM sanctions screening (both annexes)
  // before the contracts/recap can be minted. Server-enforced too (400).
  const [secoConfirmed, setSecoConfirmed] = useState(false);
  // Additive, informational-only: per-partner in-house SECO screening summary.
  // Hits do NOT block the recap (the tick stays required either way); they just
  // warn the trader to review on the partner's KYC tab.
  const [partnerScreenings, setPartnerScreenings] = useState([]);

  // Re-init form when the modal is (re)opened or the underlying enquiry
  // changes (e.g. a side-panel edit happened before the modal was opened).
  useEffect(() => {
    if (isOpen) {
      setSale(buildInitialSide(enquiry, 'sale'));
      setPurchase(buildInitialSide(enquiry, 'purchase'));
      setAuditNotes('');
      setLocalErrors({ sale: {}, purchase: {} });
      setSecoConfirmed(false);
    }
  }, [isOpen, enquiry]);

  // Fetch the per-partner SECO screening summary when the modal opens (the
  // clearance-status endpoint carries it additively). Best-effort: a failure
  // simply hides the summary line and never blocks the recap.
  useEffect(() => {
    let cancelled = false;
    if (isOpen && enquiry?.id) {
      fuelEnquiryService
        .getKycClearanceStatus(enquiry.id)
        .then((data) => {
          if (!cancelled) setPartnerScreenings(data?.partner_screenings || []);
        })
        .catch(() => {
          if (!cancelled) setPartnerScreenings([]);
        });
    } else {
      setPartnerScreenings([]);
    }
    return () => { cancelled = true; };
  }, [isOpen, enquiry]);

  // Derive the SECO screening summary line shown above the confirmation tick.
  const secoScreeningSummary = useMemo(() => {
    if (!partnerScreenings.length) return null;
    // No SECO list has ever been imported -> a screening ran (or would run)
    // against an empty table. Never present that as green "screened clear";
    // surface an amber "unavailable" notice. The confirmation tick is still
    // required (canSubmit gates on secoConfirmed regardless).
    const noList = partnerScreenings.some((p) => p.screening?.status === 'no_list');
    if (noList) {
      return {
        tone: 'nolist',
        text: 'SECO screening unavailable — no sanctions list imported yet. Confirm the manual check below.',
      };
    }
    const withHits = partnerScreenings.filter((p) => {
      const s = p.screening;
      return s && ((s.hit_count || 0) + (s.possible_count || 0)) > 0;
    });
    const listDate = partnerScreenings
      .map((p) => p.screening?.list_version_date)
      .find(Boolean) || null;
    if (withHits.length) {
      const total = withHits.reduce(
        (acc, p) => acc + (p.screening.hit_count || 0) + (p.screening.possible_count || 0),
        0,
      );
      const names = withHits.map((p) => p.name).join(', ');
      return {
        tone: 'warning',
        text: `${total} potential SECO ${total === 1 ? 'hit' : 'hits'} on ${names} — review on the partner's KYC tab.`,
      };
    }
    const allScreened = partnerScreenings.every((p) => p.screening);
    if (allScreened) {
      return {
        tone: 'clear',
        text: `All partners screened clear${listDate ? ` (list ${listDate})` : ''}.`,
      };
    }
    return null;
  }, [partnerScreenings]);

  // --- File handling helpers -----------------------------------------------

  const addFile = (target, list, setList) => (file) => {
    const validationError = validateFile(file);
    const entry = {
      localId: `${target}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      status: validationError ? 'invalid' : 'queued',
      progress: 0,
      error: validationError,
      // Populated post-advance when the parent attempts the upload.
      uploadedId: null,
    };
    setList([...list, entry]);
  };

  const removeFile = (list, setList) => (localId) => {
    setList(list.filter((entry) => entry.localId !== localId));
  };

  const handleSaleFileAdd = addFile('sale', saleFiles, onSaleFilesChange);
  const handlePurchaseFileAdd = addFile('purchase', purchaseFiles, onPurchaseFilesChange);
  const handleSaleFileRemove = removeFile(saleFiles, onSaleFilesChange);
  const handlePurchaseFileRemove = removeFile(purchaseFiles, onPurchaseFilesChange);

  // --- Validation + submit -------------------------------------------------

  const validateSide = (state) => {
    const errs = {};
    if (!state.entity_id) errs.entity_id = 'Required';
    if (!state.contract_type) errs.contract_type = 'Required';
    if (!state.contract_position) errs.contract_position = 'Required';
    if (!state.commence_date) errs.commence_date = 'Required';
    if (state.period_start && state.period_end && state.period_start > state.period_end) {
      errs.period_end = 'End must be on or after start';
    }
    return errs;
  };

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!secoConfirmed) return false;
    if (!sale.entity_id || !purchase.entity_id) return false;
    if (!sale.contract_type || !purchase.contract_type) return false;
    if (!sale.contract_position || !purchase.contract_position) return false;
    if (!sale.commence_date || !purchase.commence_date) return false;
    const invalidFiles = [...saleFiles, ...purchaseFiles].filter((e) => e.status === 'invalid');
    if (invalidFiles.length > 0) return false;
    return true;
  }, [submitting, secoConfirmed, sale, purchase, saleFiles, purchaseFiles]);

  const handleConfirm = async () => {
    const saleErrors = validateSide(sale);
    const purchaseErrors = validateSide(purchase);
    if (Object.keys(saleErrors).length || Object.keys(purchaseErrors).length) {
      setLocalErrors({ sale: saleErrors, purchase: purchaseErrors });
      return;
    }
    setLocalErrors({ sale: {}, purchase: {} });

    const buildPayload = (state) => ({
      entity_id: Number(state.entity_id),
      contract_type: state.contract_type,
      contract_position: state.contract_position,
      commence_date: state.commence_date,
      period_start: state.period_start || null,
      period_end: state.period_end || null,
      term_notes: (state.term_notes || '').trim(),
    });

    await onConfirm({
      contract_payloads: {
        sale: buildPayload(sale),
        purchase: buildPayload(purchase),
      },
      notes: auditNotes.trim(),
      seco_confirmed: secoConfirmed,
      saleFiles,
      purchaseFiles,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fef-modal-overlay"
      onClick={() => !submitting && onClose?.()}
    >
      <div
        className="fef-modal fef-modal--wide legal-contract-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-contract-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="fef-modal-header">
          <h3 className="fef-modal-title" id="legal-contract-modal-title">
            <FontAwesomeIcon icon={faGavel} /> Create Bunker Contracts
          </h3>
          <button
            type="button"
            className="fef-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        <div className="fef-modal-body legal-contract-modal__body">
          <p className="fef-modal-prompt">
            Two C-numbers will be assigned in sequence (Sale first, then
            Purchase). Both legs are minted atomically — if anything
            fails, no numbers are consumed.
          </p>

          <div className="legal-modal-split">
            <SidePanel
              side="sale"
              title="Sale Contract"
              state={sale}
              setState={setSale}
              files={saleFiles}
              onFileAdd={handleSaleFileAdd}
              onFileRemove={handleSaleFileRemove}
              hmsEntities={hmsEntities}
              errors={localErrors.sale}
              disabled={submitting}
            />
            <SidePanel
              side="purchase"
              title="Purchase Contract"
              state={purchase}
              setState={setPurchase}
              files={purchaseFiles}
              onFileAdd={handlePurchaseFileAdd}
              onFileRemove={handlePurchaseFileRemove}
              hmsEntities={hmsEntities}
              errors={localErrors.purchase}
              disabled={submitting}
            />
          </div>

          <Field label="Notes for audit trail" hint="Optional">
            <textarea
              className="fef-modal-notes"
              value={auditNotes}
              onChange={(e) => setAuditNotes(e.target.value)}
              disabled={submitting}
              rows={2}
              placeholder="Any context for the stage advance history…"
            />
          </Field>

          {/* Compulsory SECO SESAM sanctions confirmation. Recapping records a
            * SECO check per linked partner, so the trader confirms the
            * screening (both annexes) was done. Scoped label markup (no
            * <form className="hms-stacked-form">) to avoid the app-wide unscoped form{} CSS leak. */}
          <div className="legal-contract-modal__seco">
            {secoScreeningSummary && (
              <div className={`legal-contract-modal__seco-screening legal-contract-modal__seco-screening--${secoScreeningSummary.tone}`}>
                {secoScreeningSummary.text}
              </div>
            )}
            <label className="legal-contract-modal__seco-check">
              <input
                type="checkbox"
                checked={secoConfirmed}
                onChange={(e) => setSecoConfirmed(e.target.checked)}
                disabled={submitting}
              />
              <span>
                SECO SESAM sanctions screening completed for all partners
                (both annexes checked).{' '}
                <a
                  href="https://www.seco.admin.ch/seco/en/home/Aussenwirtschaftspolitik_Wirtschaftliche_Zusammenarbeit/Wirtschaftsbeziehungen/exportkontrollen-und-sanktionen/sanktionen-embargos.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open the SECO sanctions tool
                </a>
              </span>
            </label>
          </div>

          {error && (
            <div className="fef-modal-error" role="alert">{error}</div>
          )}
        </div>

        <footer className="fef-modal-footer">
          <button
            type="button"
            className="fef-modal-btn fef-modal-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="fef-modal-btn fef-modal-btn-primary"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting ? (
              <>
                {/* Inline spinner kept as a sibling <span> rather than
                  * <LoadingSpinner> so the button doesn't inherit the
                  * spinner component's column layout + 48px padding,
                  * which was ballooning the control on submit. */}
                <span
                  className="fef-modal-btn-spinner"
                  aria-hidden="true"
                />
                <span>Creating contracts…</span>
              </>
            ) : (
              <>Continue</>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ContractCreatorModal;
